import React from 'react';

import {
    AlertTriangleIcon,
    ClockIcon,
    DownloadIcon,
    ExternalLinkIcon,
    RefreshIcon,
    ShieldOffIcon,
    SmartphoneIcon,
} from './icons';

import './styles.css';

export type WalletScreenVariant = 'no-wallet' | 'access-denied' | 'pending';

const ICONS: Record<WalletScreenVariant, React.FC> = {
    'no-wallet': AlertTriangleIcon,
    'access-denied': ShieldOffIcon,
    pending: ClockIcon,
};

interface WalletScreenContent {
    title: string;
    text: string;
    secondaryLabel?: string;
}

const CONTENT: Record<WalletScreenVariant, WalletScreenContent> = {
    'no-wallet': {
        title: 'Crypto wallet required',
        text: 'No wallet was detected on this device. An EVM wallet (e.g., MetaMask) is required to complete verification.',
        secondaryLabel: 'Check again',
    },
    'access-denied': {
        title: 'Wallet access denied',
        text: 'You declined the connection request. Please approve access to your wallet to continue verification.',
    },
    pending: {
        title: 'Check your wallet',
        text: 'A connection request is already open in MetaMask. Please open the extension and approve or reject it to continue.',
    },
};

export interface WalletScreenProps {
    variant: WalletScreenVariant;
    isMobile?: boolean;
    isRequesting?: boolean;
    primaryHref?: string;
    onPrimary?: () => void;
    onSecondary?: () => void;
}

export const WalletScreen: React.FC<WalletScreenProps> = ({
    variant,
    isMobile = false,
    isRequesting = false,
    primaryHref,
    onPrimary,
    onSecondary,
}) => {
    const { title, text, secondaryLabel } = CONTENT[variant];
    const Icon = ICONS[variant];

    const primaryLabel = (() => {
        if (variant === 'no-wallet') {
            return isMobile ? 'Open MetaMask' : 'Install MetaMask';
        }

        return isRequesting ? 'Requesting access...' : 'Try again';
    })();

    return (
        <div className='wallet-screen'>
            <div className='wallet-screen__card'>
                <div className='wallet-screen__panel'>
                    <div className='wallet-screen__icon' aria-hidden='true'>
                        <Icon />
                    </div>

                    <h1 className='wallet-screen__title'>{title}</h1>
                    <p className='wallet-screen__text'>{text}</p>
                </div>

                <div className='wallet-screen__actions'>
                    {primaryHref ? (
                        <a
                            className='wallet-screen__btn wallet-screen__btn--primary'
                            href={primaryHref}
                            target='_blank'
                            rel='noreferrer'
                        >
                            {isMobile ? <SmartphoneIcon /> : <DownloadIcon />}
                            {primaryLabel}
                            <ExternalLinkIcon />
                        </a>
                    ) : (
                        <button
                            type='button'
                            className='wallet-screen__btn wallet-screen__btn--primary'
                            onClick={onPrimary}
                            disabled={isRequesting}
                        >
                            <RefreshIcon />
                            {primaryLabel}
                        </button>
                    )}

                    {secondaryLabel && onSecondary && (
                        <button
                            type='button'
                            className='wallet-screen__btn wallet-screen__btn--secondary'
                            onClick={onSecondary}
                        >
                            <RefreshIcon />
                            {secondaryLabel}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
